import { useEffect, useMemo } from "react";
import { useFilters, usePagination, useSortBy, useTable } from "react-table";
import PropTypes from "prop-types";

import { t } from "../../../services";
import { strCode } from "../../../consts";
import { useTableData } from "../../../hooks";

import {
  FlexContainers,
  Paragraph2,
  Table,
  TablePagination,
  WrapperContainer
} from "../../../components";

import { LocationHardwareTableColumns } from "./LocationHardwareTableColumns";

const LocationHardwareTable = ({
  locationHardwareData,
  isMercury,
  searchedText,
  loading,
  error,
  onEdit
}) => {
  const data = useTableData({
    loading: loading,
    data: locationHardwareData
  });

  const columns = useMemo(() => LocationHardwareTableColumns(isMercury), [isMercury]);
  const tableInstance = useTable(
    {
      columns,
      data
    },
    useFilters,
    useSortBy,
    usePagination
  );

  const {
    rows, // total rows
    setFilter,
    nextPage, // pagination props
    previousPage,
    canNextPage,
    canPreviousPage,
    gotoPage,
    pageCount,
    setPageSize,
    state: { pageIndex, pageSize }
  } = tableInstance;

  useEffect(() => {
    setFilter("locationName", searchedText);
  }, [searchedText]);

  return (
    <WrapperContainer width="100%">
      {error ? (
        <WrapperContainer
          Container={FlexContainers.FlexColumnCenter}
          width={"100%"}
          mt={200}
          textAlign={"center"}>
          <Paragraph2>{t(strCode.HARDWARE_FETCH_ERROR)}</Paragraph2>
        </WrapperContainer>
      ) : (
        locationHardwareData && (
          <>
            <Table
              tableKey="location-hardware-table"
              tableInstance={tableInstance}
              searchedText={searchedText}
              searchInfo={t(strCode.NO_LOCATIONS_FOUND, { locationName: searchedText })}
              searchCheckInfo={t(strCode.CHECK_LOCATION_NAME_INFO)}
              tooltipLabel={t(strCode.EDIT)}
              isLoading={loading && locationHardwareData?.length === 0}
              callbackFn1={onEdit}
              hasPagination
            />
            <TablePagination
              tableKey="location-hardware-table"
              tableSize={rows?.length}
              pageIndex={pageIndex}
              pageSize={pageSize}
              setPageSize={setPageSize}
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              onPrevPage={previousPage}
              onNextPage={nextPage}
              onFirstPage={() => gotoPage(0)}
              onLastPage={() => gotoPage(pageCount - 1)}
            />
          </>
        )
      )}
    </WrapperContainer>
  );
};

LocationHardwareTable.propTypes = {
  locationHardwareData: PropTypes.arrayOf(
    PropTypes.shape({
      locationId: PropTypes.string.isRequired,
      locationName: PropTypes.string.isRequired,
      hardware: PropTypes.arrayOf(PropTypes.string),
      market: PropTypes.string
    })
  ),
  isMercury: PropTypes.bool.isRequired,
  searchedText: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired
};

export default LocationHardwareTable;
